import { Button, Input } from "antd";
import styles from "./search.module.less";
import { SearchOutlined } from "@ant-design/icons";
import {
  useRef,
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
  useMemo,
} from "react";
import classNames from "classnames";
import { useRouter } from "next/router";
import { isEnter } from "@/utils/keyBoard";
import HBIcon from "@/components/HBIcon";

/**
 * 搜索输入框
 * @param {Object} props 和antd的Input属性一致
 * @param {function} props.onSearch 回车、点击搜索按钮
 * @param {boolean} props.placeholderCenter 设置placeholder居中显示
 * @returns {JSX.Element}
 */
function HBSearchInput(
  {
    hideSearchBtn = false,
    onSearch,
    placeholderCenter = false,
    preTags = [],
    ...props
  } = {},
  ref
) {
  const [isFocus, setFocus] = useState(false);

  const [isFilled, setFilled] = useState(false);

  // 当前拼音输入的参数
  const [pinyininput, setPinyin] = useState(false);

  const preTagsRef = useRef(null);
  const [preTagsOffset, setPretagsOffset] = useState(0);

  const inputRef = useRef(null);

  useImperativeHandle(
    ref,
    () => ({
      input: inputRef.current.input,
    }),
    [inputRef]
  );

  useEffect(() => {
    if (props.value) {
      setFilled(true);
    } else {
      setFilled(false);
    }
  }, [props.value]);

  const router = useRouter();
  const isHome = router?.pathname === "/";

  const prefix = <SearchOutlined />;
  const suffix = hideSearchBtn ? null : (
    <Button
      className={styles.btn}
      type="primary"
      onClick={(e) => {
        inputRef.current.input.blur();
        onSearch && onSearch(inputRef.current.input.value);
        e.stopPropagation();
      }}
    >
      <SearchOutlined />
    </Button>
  );

  const tags = useMemo(() => {
    return preTags.filter((tag) => tag.label);
  }, [preTags]);

  useEffect(() => {
    if (tags && tags.length && preTagsRef.current) {
      setPretagsOffset(preTagsRef.current.offsetWidth - 20);
    } else {
      setPretagsOffset(0);
    }
  }, [tags]);

  return (
    <div
      className={classNames(styles.wrap, {
        [styles.filled]: isFilled,
        [styles.focus]: isFocus,
      })}
      style={props.style || {}}
    >
      {
        // 搜索范围标签
        tags && tags.length > 0 && (
          <div className={styles.preTages} ref={preTagsRef}>
            {tags.map((tag) => {
              if (tag) {
                return (
                  <span key={tag.label} className={styles.tag}>
                    {tag.label}
                    <HBIcon
                      type="ic_close"
                      onClick={() => {
                        tag.clear(null);
                      }}
                    />
                  </span>
                );
              }
            })}
          </div>
        )
      }

      <div
        className={classNames(styles.placeholder, {
          [styles.center]: placeholderCenter,
        })}
      >
        {props.placeholder || "搜索"}
      </div>

      <Input
        allowClear={!isHome}
        ref={inputRef}
        prefix={prefix}
        suffix={suffix}
        {...props}
        placeholder=""
        className={classNames(styles.search, {
          [styles.hasSearchBtn]: !hideSearchBtn,
        })}
        style={{ textIndent: `${preTagsOffset}px` }}
        autoComplete="off"
        onFocus={(e) => {
          setFocus(true);
          props.onFocus && props.onFocus(e);
        }}
        onBlur={(e) => {
          setFocus(false);
          if (e.target.value) {
            setFilled(true);
          } else {
            setFilled(false);
          }
          props.onBlur && props.onBlur(e);
        }}
        onKeyDown={(e) => {
          if (isEnter(e) && !pinyininput) {
            onSearch && onSearch(e.target.value);
            e.target.blur();
          }
          props.onKeyDown && props.onKeyDown(e);
        }}
        onInput={(e) => {
          if (e.target.value) {
            setFilled(true);
          } else {
            setFilled(false);
          }
          props.onInput && props.onInput(e);
        }}
        onCompositionStart={() => setPinyin(true)}
        onCompositionEnd={() =>
          // 没有异步在火狐浏览器里无法输入中文
          setTimeout(() => {
            setPinyin(false);
          }, 1)
        }
      />
    </div>
  );
}

export default forwardRef(HBSearchInput);
